import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, ListChecks, X } from "lucide-react";

type Quest = {
  id: string; title: string; description: string | null;
  steps: { label: string }[]; reward_points: number; reward_amount_cents: number;
  reward_badge: string | null; status: string; creator_id: string;
};

export default function CreatorQuests() {
  const { user } = useAuth();
  const [quests, setQuests] = useState<Quest[]>([]);
  const [progress, setProgress] = useState<Record<string, number[]>>({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", reward_points: "100", reward_amount_cents: "0", reward_badge: "" });
  const [steps, setSteps] = useState<string[]>(["", ""]);

  const load = async () => {
    const { data } = await supabase.from("creator_quests").select("*").eq("status", "active").order("created_at", { ascending: false });
    const normalized = ((data as any[]) || []).map((q) => {
      let s: any = q.steps;
      if (typeof s === "string") { try { s = JSON.parse(s); } catch { s = []; } }
      if (!Array.isArray(s)) s = [];
      return { ...q, steps: s.map((x: any) => (typeof x === "string" ? { label: x } : x)) } as Quest;
    });
    setQuests(normalized);
    if (user) {
      const { data: prog } = await supabase.from("quest_progress").select("quest_id, completed_steps").eq("fan_id", user.id);
      const map: Record<string, number[]> = {};
      (prog as any[] | null)?.forEach((p) => { map[p.quest_id] = p.completed_steps || []; });
      setProgress(map);
    }
  };
  useEffect(() => { load(); }, [user]);

  const submit = async () => {
    if (!user || !form.title.trim()) { toast.error("Title required"); return; }
    const cleaned = steps.map((s) => s.trim()).filter(Boolean).map((label) => ({ label }));
    if (cleaned.length < 2) { toast.error("Add at least 2 steps"); return; }
    setBusy(true);
    const { error } = await supabase.from("creator_quests").insert({
      creator_id: user.id,
      title: form.title.trim(),
      description: form.description.trim() || null,
      steps: cleaned,
      reward_points: parseInt(form.reward_points || "0") || 0,
      reward_amount_cents: Math.round(parseFloat(form.reward_amount_cents || "0") * 100),
      reward_badge: form.reward_badge.trim() || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Quest live");
    setOpen(false);
    setForm({ title: "", description: "", reward_points: "100", reward_amount_cents: "0", reward_badge: "" });
    setSteps(["", ""]);
    load();
  };

  const toggleStep = async (q: Quest, idx: number, checked: boolean) => {
    if (!user) return;
    const current = new Set(progress[q.id] || []);
    if (checked) current.add(idx); else current.delete(idx);
    const arr = Array.from(current).sort();
    const allDone = arr.length === q.steps.length;
    const { error } = await supabase.from("quest_progress").upsert({
      fan_id: user.id, quest_id: q.id,
      completed_steps: arr, completed_at: allDone ? new Date().toISOString() : null,
    }, { onConflict: "fan_id,quest_id" });
    if (error) { toast.error(error.message); return; }
    setProgress({ ...progress, [q.id]: arr });
    if (allDone) toast.success(`Quest complete: ${q.title}`);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/creator-mode"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold flex-1">Quest Chains</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm"><Plus className="size-4 mr-1" />New</Button></DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create a quest</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={400} /></div>
                <div className="space-y-2">
                  <Label>Steps</Label>
                  {steps.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={s} onChange={(e) => { const a = [...steps]; a[i] = e.target.value; setSteps(a); }} placeholder={`Step ${i + 1}`} maxLength={120} />
                      {steps.length > 2 && <Button type="button" size="icon" variant="ghost" onClick={() => setSteps(steps.filter((_, j) => j !== i))}><X className="size-4" /></Button>}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setSteps([...steps, ""])}>Add step</Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Points</Label><Input type="number" value={form.reward_points} onChange={(e) => setForm({ ...form, reward_points: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Cash</Label><Input type="number" value={form.reward_amount_cents} onChange={(e) => setForm({ ...form, reward_amount_cents: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Badge</Label><Input value={form.reward_badge} onChange={(e) => setForm({ ...form, reward_badge: e.target.value })} /></div>
                </div>
                <Button onClick={submit} disabled={busy} className="w-full">{busy ? "Saving…" : "Create quest"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {quests.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground"><ListChecks className="size-10 mx-auto mb-2" />No quests yet.</CardContent></Card>
        ) : quests.map((q) => {
          const done = progress[q.id] || [];
          const pct = q.steps.length ? Math.round((done.length / q.steps.length) * 100) : 0;
          const mine = q.creator_id === user?.id;
          return (
            <Card key={q.id}>
              <CardHeader className="pb-2"><CardTitle className="text-base">{q.title}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {q.description && <p className="text-sm text-muted-foreground">{q.description}</p>}
                <Progress value={pct} className="h-2" />
                <div className="space-y-1.5">
                  {q.steps.map((step, i) => (
                    <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={done.includes(i)} onCheckedChange={(c) => !mine && toggleStep(q, i, !!c)} disabled={mine} />
                      <span className={done.includes(i) ? "line-through text-muted-foreground" : ""}>{step.label}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                  <span>{q.reward_points} pts {q.reward_amount_cents > 0 && `+ $${(q.reward_amount_cents / 100).toFixed(2)}`} {q.reward_badge && `• 🏅 ${q.reward_badge}`}</span>
                  {mine && <Badge variant="outline">Yours</Badge>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>
    </div>
  );
}
